import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Type definitions
interface EmailTemplate {
  id: number;
  name: string;
  description: string;
  subject: string;
  textContent: string;
  htmlContent: string;
  type: string;
  variables: string[] | any; // Handle different variable formats
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

const EmailTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const [previewData, setPreviewData] = useState<{
    template: EmailTemplate | null;
    variables: Record<string, string>;
  }>({
    template: null,
    variables: {},
  });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  // Fetch all email templates
  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/email-templates'],
    queryFn: async () => {
      const response = await fetch('/api/email-templates');
      if (!response.ok) {
        throw new Error('Failed to fetch email templates');
      }
      return response.json();
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(`/api/email-templates/${id}`, 'DELETE');
      if (!response.ok) {
        throw new Error('Failed to delete template');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-templates'] });
      toast({
        title: 'Template deleted',
        description: 'The email template has been deleted successfully.',
      });
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete template',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  // Preview template mutation
  const previewTemplateMutation = useMutation({
    mutationFn: async ({
      templateContent,
      variables,
    }: {
      templateContent: string;
      variables: Record<string, string>;
    }) => {
      return apiRequest({
        url: '/api/email-templates/preview',
        method: 'POST',
        data: {
          templateContent,
          variables,
        },
      });
    },
    onSuccess: (data) => {
      setPreviewHtml(data.processedTemplate);
    },
    onError: (error) => {
      toast({
        title: 'Failed to preview template',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  // Function to handle preview
  const handlePreview = (template: EmailTemplate) => {
    // Ensure variables is an array (could be stored as an array or JSON string)
    let variablesArray: string[] = [];

    try {
      if (Array.isArray(template.variables)) {
        variablesArray = template.variables;
      } else if (typeof template.variables === 'string') {
        // Try to parse if it's a JSON string
        variablesArray = JSON.parse(template.variables);
      } else if (template.variables && typeof template.variables === 'object') {
        // If it's already an object but not an array, try to get keys
        variablesArray = Object.keys(template.variables);
      }
    } catch (e) {
      console.error('Error parsing variables:', e);
      variablesArray = [];
    }

    setPreviewData({
      template,
      variables: variablesArray.reduce(
        (acc, variable) => ({
          ...acc,
          [variable]: `[${variable}]`,
        }),
        {}
      ),
    });

    // Preview HTML content initially
    previewTemplateMutation.mutate({
      templateContent: template.htmlContent,
      variables: variablesArray.reduce(
        (acc, variable) => ({
          ...acc,
          [variable]: `Example ${variable}`,
        }),
        {}
      ),
    });

    setIsPreviewDialogOpen(true);
  };

  // Function to update preview variables
  const updatePreviewVariable = (variable: string, value: string) => {
    setPreviewData((prev) => ({
      ...prev,
      variables: {
        ...prev.variables,
        [variable]: value,
      },
    }));
  };

  // Function to regenerate preview with updated variables
  const regeneratePreview = () => {
    if (!previewData.template) return;

    previewTemplateMutation.mutate({
      templateContent: previewData.template.htmlContent,
      variables: previewData.variables,
    });
  };

  // Handle delete confirmation
  const confirmDelete = (template: EmailTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  // Execute delete
  const executeDelete = () => {
    if (templateToDelete) {
      deleteTemplateMutation.mutate(templateToDelete.id);
    }
  };

  // Seed default templates
  const seedDefaultsMutation = useMutation({
    mutationFn: async () => {
      return apiRequest({
        url: '/api/email-templates/seed-defaults',
        method: 'POST',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-templates'] });
      toast({
        title: 'Default templates seeded',
        description: 'Default email templates have been added to the database.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to seed default templates',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return <div className="container mx-auto p-6">Loading email templates...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load email templates:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Email Templates</h1>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => seedDefaultsMutation.mutate()}
            disabled={seedDefaultsMutation.isPending}
          >
            {seedDefaultsMutation.isPending ? 'Seeding...' : 'Seed Default Templates'}
          </Button>
          <Button asChild>
            <Link href="/email-templates/new">Create New Template</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Manage Email Templates</CardTitle>
          <CardDescription>
            View, edit, and manage all email templates used throughout the application.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates && templates.length > 0 ? (
                templates.map((template: EmailTemplate) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{template.type}</Badge>
                    </TableCell>
                    <TableCell>{template.description}</TableCell>
                    <TableCell>{new Date(template.updatedAt).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handlePreview(template)}>
                          Preview
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link href={`/email-templates/${template.id}/edit`}>Edit</Link>
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => confirmDelete(template)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6">
                    No email templates found. Click "Create New Template" to add one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the template "{templateToDelete?.name}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeDelete}
              disabled={deleteTemplateMutation.isPending}
            >
              {deleteTemplateMutation.isPending ? 'Deleting...' : 'Delete Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview: {previewData.template?.name}</DialogTitle>
            <DialogDescription>
              Preview how this email template will look with different variable values.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="preview">
            <TabsList className="mb-4">
              <TabsTrigger value="preview">Preview</TabsTrigger>
              <TabsTrigger value="variables">Variables</TabsTrigger>
              <TabsTrigger value="html">HTML Source</TabsTrigger>
              <TabsTrigger value="text">Text Source</TabsTrigger>
            </TabsList>

            <TabsContent
              value="preview"
              className="h-[400px] overflow-y-auto border rounded-md p-4"
            >
              {previewHtml ? (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <div className="text-center py-6">Loading preview...</div>
              )}
            </TabsContent>

            <TabsContent value="variables">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Adjust the values of template variables to see how they appear in the email.
                </p>

                {previewData.template &&
                  previewData.variables &&
                  Object.keys(previewData.variables).map((variable: string) => (
                    <div key={variable} className="grid grid-cols-4 gap-4 items-center">
                      <Label htmlFor={`var-${variable}`} className="col-span-1">
                        {variable}
                      </Label>
                      <div className="col-span-3">
                        <Input
                          id={`var-${variable}`}
                          value={previewData.variables[variable] || ''}
                          onChange={(e) => updatePreviewVariable(variable, e.target.value)}
                          placeholder={`Value for ${variable}`}
                        />
                      </div>
                    </div>
                  ))}

                <Button
                  onClick={regeneratePreview}
                  disabled={previewTemplateMutation.isPending}
                  className="mt-4"
                >
                  {previewTemplateMutation.isPending ? 'Updating Preview...' : 'Update Preview'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="html" className="h-[400px] overflow-y-auto border rounded-md p-4">
              <pre className="text-xs whitespace-pre-wrap">{previewData.template?.htmlContent}</pre>
            </TabsContent>

            <TabsContent value="text" className="h-[400px] overflow-y-auto border rounded-md p-4">
              <pre className="text-xs whitespace-pre-wrap">{previewData.template?.textContent}</pre>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
